import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Form from "react-bootstrap/Form";
import { Modal, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import OrgSettingModal from "../../../../components/modelpopup/PropertySettingModal";

const ProfileSetting = () => {
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [crop, setCrop] = useState();
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(undefined); // Off by default
  const [isCropping, setIsCropping] = useState(false);
  const [hoveredOrg, setHoveredOrg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isFullScreen, setFullScreen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const maximizeBtnRef = useRef(null);
  const imgRef = useRef(null);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const cdnUrl = process.env.REACT_APP_CDN_URL;
  const token = localStorage.getItem("token");
const [isDragging] = useState(false);
const [lastTouchDistance, setLastTouchDistance] = useState(null);

  // Fullscreen toggle
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(console.error);
        setFullScreen(true);
      } else if (document.exitFullscreen) {
        document.exitFullscreen().catch(console.error);
        setFullScreen(false);
      }
    };
    const btn = maximizeBtnRef.current;
    btn?.addEventListener("click", handleClick);
    return () => btn?.removeEventListener("click", handleClick);
  }, [isFullScreen]);

  // Fetch organizations
  useEffect(() => {
    const fetchData = async () => {
      if (!token || !apiUrl) {
        toast.error("Authentication required.");
        navigate("/login");
        return;
      }
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/company/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.organizations?.length) {
          setOrganizations(
            res.data.organizations.map((org) => ({
              id: org.company_id,
              logo: org.company_logo
                ? `${cdnUrl}/${org.company_logo}`
                : "https://via.placeholder.com/100",
              name: org.company_name || "Unnamed Organization",
              email: org.company_email || "N/A",
              type: org.type || "N/A",
            }))
          );
        } else toast.info("No properties found.");
      } catch (e) {
        toast.error("Unable to fetch organizations.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl, cdnUrl, token, navigate]);

  const onDrop = useCallback((accepted) => {
    const file = accepted[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Upload a valid image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max file size 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
  });

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const newCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspect || width / height, width, height),
      width,
      height
    );
    setCrop(newCrop);
    setZoom(1);
  };

  
  const handleModalSubmit = useCallback((updatedOrg) => {
    const company = updatedOrg.company;
    const { logo, ...filteredCompany } = Object.keys(company).reduce((acc, key) => {
      if (!key.startsWith('company_logo_') && key !== 'logo') {
        acc[key] = company[key];
      }
      return acc;
    }, {});
    setOrganizations((prevOrgs) => {
      const orgIndex = prevOrgs.findIndex((org) => org.id === filteredCompany.companyid);
      const updatedOrgData = {
        ...prevOrgs[orgIndex] || {},
        ...filteredCompany,
        id: filteredCompany.companyid,
      };
      if (orgIndex > -1) {
        if (JSON.stringify(prevOrgs[orgIndex]) === JSON.stringify(updatedOrgData)) {
          console.log('No changes detected, skipping state update');
          return prevOrgs;
        }
        return prevOrgs.map((org, index) => (index === orgIndex ? updatedOrgData : org));
      } else {
        return [...prevOrgs, updatedOrgData];
      }
    });
    setShowModal(false);
  }, []);

    const handleDeleteClick = useCallback((orgId) => {
    Swal.fire({
      title: 'Confirm Deletion',
      text: 'All data related to this organization will be permanently deleted!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete Organization',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${apiUrl}/company/delete/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrganizations((prev) => prev.filter((org) => org.id !== orgId));
          toast.success('Organization deleted successfully.');
        } catch (error) {
          console.error('Error deleting organization:', error);
          toast.error('Failed to delete organization.');
        }
      }
    });
  }, [apiUrl, token]);

  // Switch organization with confirmation
  const handleSwitchCompany = useCallback((orgId, orgName) => {
    Swal.fire({
      title: 'Confirm Organization Switch',
      text: `Switch to ${orgName}? This will update your active session.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Switch Organization',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.post(
            `${apiUrl}/users/switch-company`,
            { companyid: orgId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.status === 200) {
            const data = response.data;
            const sessionItems = {
              token: data.accessToken,
              refreshToken: data.refreshToken,
              companyid: data.companyid,
              company: data.company,
              role: data.role,
              app_access: JSON.stringify(data.app_access || []),
              companies: JSON.stringify(data.companies),
              dateFormat: data.dateformat,
              dateFormatWithTime: data.dateFormatWithTime,
              timezone: data.timezone,
              currencycode: data.currency,
              business_date: data.business_date,
              posbusiness_date: data.posBusinessdate,
              companylogo : data.logo,

              
            };
            Object.entries(sessionItems).forEach(([key, value]) => {
              localStorage.setItem(key, value);
            });
            //localStorage.removeItem('token'); // Clear token after switching
            toast.success(`Successfully switched to ${orgName}`);
                      localStorage.removeItem("selectedRooms");
                    localStorage.removeItem("billingSummary");
                    localStorage.removeItem("searchState");
                    localStorage.removeItem("searchParams");
                    localStorage.removeItem("filteredRooms");
                      window.location.reload(); // Refresh the current page
            
          }
        } catch (error) {
          console.error('Error switching organization:', error);
          toast.error(error.response?.data?.message || 'Failed to switch organization');
        }
      }
    });
  }, [apiUrl, token, navigate]);

  const handleEditClick = useCallback((org) => {
    setSelectedOrg(org);
    setShowModal(true);
  }, []);
  const handleImageChange = useCallback((e, org) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedOrg(org);
      setPreviewImage(null);
      setCrop(undefined);
      setCroppedAreaPixels(null);
      setCroppedBlob(null);
      setZoom(1);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  }, []);
const generateCroppedImage = useCallback(() => {
  if (!imgRef.current || !croppedAreaPixels) return;

  const image = imgRef.current;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Adjust crop based on zoom
  const adjustedX = croppedAreaPixels.x * scaleX / zoom;
  const adjustedY = croppedAreaPixels.y * scaleY / zoom;
  const adjustedWidth = croppedAreaPixels.width * scaleX / zoom;
  const adjustedHeight = croppedAreaPixels.height * scaleY / zoom;

  canvas.width = adjustedWidth;
  canvas.height = adjustedHeight;

  // Center image according to zoom level
  const displayWidth = image.naturalWidth / zoom;
  const displayHeight = image.naturalHeight / zoom;
  const offsetX = (displayWidth - image.naturalWidth) / 2;
  const offsetY = (displayHeight - image.naturalHeight) / 2;

  ctx.drawImage(
    image,
    adjustedX - offsetX,
    adjustedY - offsetY,
    adjustedWidth,
    adjustedHeight,
    0,
    0,
    adjustedWidth,
    adjustedHeight
  );

  canvas.toBlob((blob) => blob && setCroppedBlob(blob), "image/png");
}, [croppedAreaPixels, zoom]);


  useEffect(() => {
    generateCroppedImage();
  }, [generateCroppedImage]);

  const handleCropSave = async () => {
    if (!selectedOrg?.id || !croppedBlob) {
      toast.error("Please crop an image first.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("image", croppedBlob, "logo.png");
      formData.append("orgid", selectedOrg.id);
      const res = await axios.post(`${apiUrl}/company/upload-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        const { logoUrl } = res.data;
        setOrganizations((prev) =>
          prev.map((o) => (o.id === selectedOrg.id ? { ...o, logo: logoUrl } : o))
        );
        toast.success("Logo updated.");
      }
    } catch (e) {
      toast.error("Upload failed.");
    } finally {
      setIsCropping(false);
      setPreviewImage(null);
    }
  };

  const handleCancel = () => {
    setIsCropping(false);
    setPreviewImage(null);
  };

  useEffect(() => {
    if (!showModal && !isCropping) return undefined;

    document.body.classList.add("ns-manage-property-modal-open");

    return () => {
      document.body.classList.remove("ns-manage-property-modal-open");
    };
  }, [showModal, isCropping]);

  return (
    <div className="page-wrapper ns-property-page-wrapper">
      <div className="content container-fluid ns-property-page">
        <style>{`
          body.ns-manage-property-modal-open {
            overflow: hidden !important;
          }

          .ns-property-page-wrapper {
            margin-top: 70px; !important;
            padding-top: 0 !important;
            background:
              radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.07), transparent 30%),
              radial-gradient(circle at 92% 2%, rgba(34, 197, 94, 0.08), transparent 28%),
              #f8fafc;
          }

          .ns-property-page {
            padding-top: 12px !important;
            margin-top: 0 !important;
            padding-bottom: 24px;
            color: #0f172a;
          }

          .ns-property-hero {
            background:
              radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 34%),
              radial-gradient(circle at top right, rgba(34, 197, 94, 0.10), transparent 32%),
              linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 24px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
            padding: 17px 18px;
            margin-bottom: 12px;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            flex-wrap: wrap;
          }

          .ns-property-hero-left {
            display: flex;
            align-items: flex-start;
            gap: 13px;
            min-width: 0;
          }

          .ns-property-hero-icon {
            width: 52px;
            height: 52px;
            border-radius: 19px;
            background: linear-gradient(135deg, #1d4ed8, #2563eb);
            color: #ffffff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 16px 34px rgba(37, 99, 235, 0.22);
            flex: 0 0 auto;
            font-size: 1.35rem;
          }

          .ns-property-eyebrow {
            color: #2563eb;
            font-size: 0.72rem;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            font-weight: 950;
            margin-bottom: 5px;
          }

          .ns-property-hero h2 {
            color: #001f5b;
            font-size: 1.42rem;
            line-height: 1.16;
            font-weight: 950;
            margin: 0;
          }

          .ns-property-hero p {
            color: #64748b;
            font-size: 0.82rem;
            line-height: 1.5;
            font-weight: 760;
            margin: 7px 0 0;
            max-width: 880px;
          }

          .ns-property-primary-btn,
          .ns-property-soft-btn {
            min-height: 42px;
            border-radius: 14px;
            border: 1px solid rgba(15, 23, 42, 0.10);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 0 16px;
            font-size: 0.82rem;
            font-weight: 950;
            cursor: pointer;
            white-space: nowrap;
          }

          .ns-property-primary-btn {
            background: linear-gradient(135deg, #1d4ed8, #2563eb);
            border-color: #1d4ed8;
            color: #ffffff;
            box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
          }

          .ns-property-page > .row {
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 24px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
            padding: 14px;
            margin: 0;
          }

          .ns-property-hero + .row {
            margin-top: 0 !important;
          }

          .ns-property-page .profile-widget {
            min-height: 262px !important;
            border: 1px solid rgba(15, 23, 42, 0.08) !important;
            border-top: 4px solid #94a3b8 !important;
            border-radius: 22px !important;
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06) !important;
            background:
              radial-gradient(circle at top right, rgba(37, 99, 235, 0.06), transparent 34%),
              #ffffff !important;
            padding: 16px 14px 14px !important;
            transition: transform 0.16s ease, box-shadow 0.16s ease;
          }

          .ns-property-page .profile-widget:hover {
            transform: translateY(-2px);
            box-shadow: 0 18px 42px rgba(15, 23, 42, 0.11) !important;
          }

          .ns-property-page .profile-widget[style*="f0f8ff"] {
            border-top-color: #22c55e !important;
            background:
              radial-gradient(circle at top right, rgba(34, 197, 94, 0.10), transparent 34%),
              #ffffff !important;
          }

          .ns-property-page .profile-img {
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 18px !important;
            background: #f8fafc !important;
            margin-bottom: 14px !important;
          }

          .ns-property-page .organization-name {
            color: #001f5b !important;
            font-size: 1.02rem !important;
            font-weight: 950 !important;
          }

          .ns-property-page .organization-email,
          .ns-property-page .organization-phone {
            color: #64748b !important;
            font-size: 0.78rem !important;
            font-weight: 780 !important;
          }

          .ns-property-page .dropdown.profile-action {
            top: 10px !important;
            right: 10px !important;
          }

          .ns-property-page .action-icon {
            width: 34px;
            height: 34px;
            border-radius: 13px;
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
          }

          .ns-property-page .dropdown-menu {
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 16px;
            box-shadow: 0 18px 42px rgba(15, 23, 42, 0.14);
            padding: 8px;
          }

          .ns-property-page .dropdown-item {
            border: 0;
            background: transparent;
            border-radius: 12px;
            font-weight: 850;
            color: #334155;
            width: 100%;
            text-align: left;
            padding: 8px 10px;
          }

          .ns-property-page .btn-success {
            border-radius: 14px !important;
            background: linear-gradient(135deg, #16a34a, #22c55e) !important;
            border-color: #16a34a !important;
            font-weight: 900 !important;
          }

          .ns-crop-modal .modal-content {
            border: 0;
            border-radius: 24px;
            overflow: hidden;
            box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
          }

          .ns-crop-modal .modal-header {
            background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
            border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          }

          .ns-crop-modal .modal-title {
            color: #001f5b;
            font-weight: 950;
          }

          .ns-crop-dropzone {
            min-height: 260px;
            border: 2px dashed #bfdbfe;
            border-radius: 22px;
            background: #ffffff;
            color: #64748b;
            padding: 36px;
            text-align: center;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }

          .ns-crop-dropzone.active {
            border-color: #2563eb;
            background: #eff6ff;
            color: #1d4ed8;
          }

          .ns-crop-preview {
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 22px;
            padding: 12px;
            text-align: center;
          }

          .ns-crop-controls {
            width: min(520px, 100%);
            margin: 18px auto 0;
            display: grid;
            gap: 12px;
          }

          .ns-crop-zoom-row {
            display: grid;
            grid-template-columns: auto 1fr auto;
            gap: 10px;
            align-items: center;
          }

          .swal2-popup {
            border-radius: 22px !important;
          }

          @media (max-width: 640px) {
            .ns-property-page {
              padding-left: 8px;
              padding-right: 8px;
            }

          }
        `}</style>

        <section className="ns-property-hero">
          <div className="ns-property-hero-left">
            <span className="ns-property-hero-icon">
              <i className="fa fa-building-o" />
            </span>

            <div>
              <div className="ns-property-eyebrow">Property Workspace</div>
              <h2>Manage Properties</h2>
              <p>
                Manage company profiles, logos, compliance fields, app access, billing configuration,
                and active property switching from one polished workspace.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="ns-property-primary-btn"
            onClick={() => {
              setSelectedOrg(null);
              setShowModal(true);
            }}
          >
            <i className="la la-plus-circle" />
            Add Property
          </button>
        </section>
        <div className="row">
          {loading ? (
            <div id="loader-wrapper-" style={{ textAlign: 'center', margin: '20px 0' }}>
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : organizations.length === 0 ? (
            <div style={{ textAlign: 'center', margin: '20px 0', color: '#6c757d' }}>
              No properties found.
            </div>
          ) : (
            organizations.map((org) => (
              <div
                key={org.id}
                className="col-md-4 col-sm-6 col-12"
                style={{ borderRadius: '5px', marginBottom: '20px' }}
              >
                <div
                  className="profile-widget"
                  style={{
                    borderRadius: '5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    backgroundColor: org.type === 'Current' ? '#f0f8ff' : '#fff',
                  }}
                >
                  {org.type === 'Current' && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '80%',
                        left: 0,
                        transform: 'translateY(-50%) rotate(-90deg)',
                        transformOrigin: 'left center',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        padding: '0px 15px',
                        borderRadius: '5px 5px 0 0',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        marginLeft: '10px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Current Property
                    </div>
                  )}
                  <div
                    className="profile-img"
                    style={{
                      width: '210px',
                      height: '80px',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0',
                      margin: 'auto',
                    }}
                    onMouseEnter={() => setHoveredOrg(org.id)}
                    onMouseLeave={() => setHoveredOrg(null)}
                  >
                    <img
                      src={org.logo}
                      alt={`${org.name} Logo`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '0',
                      }}
                    />
                    {hoveredOrg === org.id && (
                      <div
                        className="profile-img-hover"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'rgba(165, 152, 152, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10,
                          borderRadius: '0',
                          cursor: 'pointer',
                        }}
                        onClick={() => document.getElementById(`image-${org.id}`)?.click()}
                      >
                        <i
                          className="fa fa-camera"
                          style={{ fontSize: '24px', color: 'white' }}
                          aria-hidden="true"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      id={`image-${org.id}`}
                      className="d-none"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, org)}
                      aria-label={`Upload logo for ${org.name}`}
                    />
                  </div>
                  <div className="dropdown profile-action">
                    <Link
                      to="#"
                      className="action-icon dropdown-toggle"
                      data-bs-toggle="dropdown"
                      aria-label="More actions"
                    >
                      <i className="material-icons">more_vert</i>
                    </Link>
                    <div className="dropdown-menu dropdown-menu-right">
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          handleEditClick(org);
                        }}
                      >
                        <i className="fa fa-pencil m-r-5" /> Edit
                      </button>

                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleDeleteClick(org.id)}
                        aria-label={`Delete ${org.name}`}
                      >
                        <i className="fa fa-trash m-r-5" /> Delete
                      </button>
                    </div>
                  </div>
                  <h4
                    className="organization-name"
                    style={{ marginBottom: '8px', fontWeight: 'bold', textAlign: 'center' }}
                  >
                    {org.name}
                  </h4>
                  <p className="organization-phone" style={{ marginBottom: '4px', textAlign: 'center' }}>
                    {org.phone}
                  </p>
                  <p className="organization-email" style={{ marginBottom: '0', textAlign: 'center' }}>
                    {org.email}
                  </p>
                  <hr style={{ borderTop: '1px solid #ddd', margin: '6px 0' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0 5px 5px',
                    }}
                  >
                    {org.type === 'Current' ? (
                      <></>
                    ) : (
                      <>
                        <div style={{ color: '#555', display: 'flex' }}>
                          <i className="fa fa-map-marker" style={{ marginRight: '5px' }} />
                          Locations #10
                        </div>
                        <div style={{ color: '#555', display: 'flex' }}>
                          <i className="fa fa-users" style={{ marginRight: '5px' }} />
                          Users #10
                        </div>
                        <button
                          className="btn btn-success"
                          style={{ padding: '5px 15px' }}
                          onClick={() => handleSwitchCompany(org.id, org.name)}
                          aria-label={`Switch ${org.name}`}
                        >
                          <i className="fa fa-random m-r-5" /> Switch Property
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
<OrgSettingModal
  show={showModal}
  showModal={showModal}
  orgData={selectedOrg}
  onSubmit={handleModalSubmit}
  onClose={() => setShowModal(false)}
/>

        <Modal show={isCropping} onHide={handleCancel} centered size="lg" dialogClassName="ns-crop-modal" backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Update Organization Logo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!previewImage ? (
              <div
                {...getRootProps()}
                className={`ns-crop-dropzone ${isDragActive ? "active" : ""}`}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>Drop image here...</p>
                ) : (
                  <>
                    <i className="fa fa-cloud-upload" style={{ fontSize: "48px", color: "#007bff" }} />
                    <p>Drag & drop or click to select</p>
                    <small>PNG, JPG, GIF (max 5MB)</small>
                  </>
                )}
              </div>
            ) : (
              <div className="ns-crop-preview">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCroppedAreaPixels(c)}
                  keepSelection
                  aspect={aspect}
                >
                  <img
  ref={imgRef}
  src={previewImage}
  alt="Crop preview"
  onLoad={onImageLoad}
  onWheel={(e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoom((z) => Math.min(Math.max(0.5, z + delta), 3));
  }}
  onTouchStart={(e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      setLastTouchDistance(Math.sqrt(dx * dx + dy * dy));
    }
  }}
  onTouchMove={(e) => {
    if (e.touches.length === 2 && lastTouchDistance) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      const diff = newDist - lastTouchDistance;
      setZoom((z) => Math.min(Math.max(0.5, z + diff / 200), 3));
      setLastTouchDistance(newDist);
    }
  }}
  onTouchEnd={() => setLastTouchDistance(null)}
  style={{
    maxWidth: "100%",
    maxHeight: "500px",
    objectFit: "contain",
    transform: `scale(${zoom})`,
    transformOrigin: "center center",
    transition: "transform 0.15s ease-out",
    cursor: isDragging ? "grabbing" : "grab",
  }}
/>

                </ReactCrop>

                {/* Zoom control */}
                <div className="ns-crop-controls">
                  <div className="ns-crop-zoom-row">
                  <label style={{ fontSize: "14px", fontWeight: 500 }}>Zoom:</label>
                  <Form.Range
                    min={0.5}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    style={{ width: "70%" }}
                  />
                  <span style={{ fontSize: "14px", minWidth: "40px", fontWeight: 900 }}>{zoom.toFixed(1)}x</span>
                  </div>
                </div>

                {/* Aspect ratio toggle */}
                <div style={{ marginTop: "10px", display: "flex", justifyContent: "center" }}>
                  <Form.Check
                    type="switch"
                    id="aspect-toggle"
                    label="Lock 16:9 Aspect"
                    checked={!!aspect}
                    onChange={(e) => setAspect(e.target.checked ? 16 / 9 : undefined)}
                  />
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            {previewImage && (
              <Button variant="primary" onClick={handleCropSave}>
                Update Logo
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ProfileSetting;
